import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import WhosInModal from './WhosInModal';
import { whosInService } from '../../services/whosInService';
import {
  buildMonthWeeks,
  buildMonthsInRange,
  formatMonthTitle,
  indexScheduleByDate,
  WEEKDAY_LABELS,
} from '../../utils/whosIn/scheduleCalendar';
import {
  SCHEDULE_STATUS_LEGEND,
  scheduleStatusLabel,
  statusBadgeClass,
} from '../../utils/whosIn/scheduleStatus';

function WhosInScheduleStatusLegend() {
  return (
    <div className="whos-in-schedule-legend" aria-label="Schedule status legend">
      {SCHEDULE_STATUS_LEGEND.map(({ label, className }) => (
        <span key={label} className="whos-in-schedule-legend-item">
          <span className={className}>{label}</span>
        </span>
      ))}
    </div>
  );
}

function ScheduleMonthGrid({ year, month, scheduleByDate, viewDate, todayIso, holidayMap }) {
  const weeks = useMemo(
    () => buildMonthWeeks(year, month, scheduleByDate),
    [year, month, scheduleByDate],
  );

  return (
    <section className="whos-in-schedule-month">
      <h4 className="whos-in-schedule-month-title">{formatMonthTitle(year, month)}</h4>
      <div className="whos-in-schedule-weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="whos-in-schedule-weekday">
            {label}
          </span>
        ))}
      </div>
      <div className="whos-in-schedule-grid">
        {weeks.flat().map((cell, idx) => {
          if (!cell.inRange || !cell.date) {
            return <div key={`pad-${year}-${month}-${idx}`} className="whos-in-schedule-day empty" />;
          }

          const schedule = cell.schedule;
          const holidayName = holidayMap?.get(cell.date);
          const isViewDate = cell.date === viewDate;
          const isToday = cell.date === todayIso;
          const dayNum = Number(cell.date.slice(8, 10));

          const classNames = [
            'whos-in-schedule-day',
            schedule ? 'scheduled' : 'unscheduled',
            isViewDate ? 'view-date' : '',
            isToday ? 'today' : '',
            holidayName ? 'holiday' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={cell.date}
              className={classNames}
              title={
                schedule
                  ? `${cell.date}: ${scheduleStatusLabel(schedule)}${holidayName ? ` (${holidayName})` : ''}`
                  : holidayName
                    ? `${cell.date}: ${holidayName}`
                    : cell.date
              }
            >
              <span className="whos-in-schedule-day-num">{dayNum}</span>
              {schedule && (
                <span className={`whos-in-schedule-day-status ${statusBadgeClass(schedule.status_code)}`}>
                  {scheduleStatusLabel(schedule)}
                </span>
              )}
              {holidayName && !schedule && (
                <span className="whos-in-schedule-day-holiday">{holidayName}</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const WhosInEmployeeScheduleModal = ({
  person,
  viewDate,
  rangeFrom,
  rangeTo,
  holidayMap,
  todayIso,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);

  const loadSchedule = useCallback(async () => {
    if (!person?.employee_id || !rangeFrom || !rangeTo) return;
    try {
      setLoading(true);
      setError(null);
      const data = await whosInService.getEmployeeSchedule(person.employee_id, rangeFrom, rangeTo);
      setScheduleData(data);
    } catch (err) {
      setError(err.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [person?.employee_id, rangeFrom, rangeTo]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const scheduleByDate = useMemo(
    () => indexScheduleByDate(scheduleData?.schedule ?? []),
    [scheduleData?.schedule],
  );

  const months = useMemo(
    () => buildMonthsInRange(rangeFrom, rangeTo),
    [rangeFrom, rangeTo],
  );

  const employee = scheduleData?.employee ?? person;
  const subtitle = [employee?.position, employee?.team || employee?.department]
    .filter(Boolean)
    .join(' · ');

  return (
    <WhosInModal
      title={employee?.name || person?.name || 'Travel schedule'}
      onClose={onClose}
      className="whos-in-schedule-modal"
    >
      {subtitle && <p className="whos-in-schedule-modal-subtitle">{subtitle}</p>}
      <p className="whos-in-schedule-modal-range hint">
        {rangeFrom} — {rangeTo}
      </p>

      {loading ? (
        <div className="whos-in-modal-loading">
          <Spinner size="medium" />
          <p>Loading travel schedule…</p>
        </div>
      ) : error ? (
        <div className="whos-in-modal-error">
          <p>{error}</p>
          <Button variant="primary" onClick={loadSchedule}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="whos-in-schedule-modal-body">
            {months.map(({ year, month }) => (
              <ScheduleMonthGrid
                key={`${year}-${month}`}
                year={year}
                month={month}
                scheduleByDate={scheduleByDate}
                viewDate={viewDate}
                todayIso={todayIso}
                holidayMap={holidayMap}
              />
            ))}
          </div>
          <WhosInScheduleStatusLegend />
          <div className="whos-in-schedule-modal-actions">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      )}
    </WhosInModal>
  );
};

export default WhosInEmployeeScheduleModal;
